import React, { ReactNode, useContext, useLayoutEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { addErrorListener } from "../../lab/src/utils/error-utils";
import { StyleguidePageProps } from "./docs-types";
import { StyleguideSideMenu } from "./docs-sidemenu";
import { DocsNotepad } from "./docs-notepad";
import * as scss from "./docs-layout.scss";

const styleguideCtx = React.createContext(null as StyleguidePageProps["pageContext"] | null);
export const useStyeguideCtx = (): StyleguidePageProps["pageContext"] => {
	const ctx = useContext(styleguideCtx);
	if (!ctx) throw "Must be used as a child to StyleguideLayout";
	return ctx;
};

function urlToPageName(url: string | undefined): string {
	if (!url) return "Design System";
	const split = url.split("/");
	const last = split[split.length - (url.endsWith("/") ? 2 : 1)]; //skip last segment if it is dash
	return last
		.split(/[ -]/) //split on space or dash
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1)) //capitalize first letter of word
		.join(" ");
}

//This is referenced in gatsby-config -> gatsby-plugin-layout and is used as the layout component (wrapper) for all gatsby pages
export default function (props: StyleguidePageProps & { children: ReactNode }): JSX.Element {
	const mainRef = useRef<HTMLElement>(null);
	useLayoutEffect(() => {
		mainRef.current!.scrollTo(0, 0); //reset scroll on render/navigation
	});

	return (
		<styleguideCtx.Provider value={props.pageContext}>
			<Helmet
				title={`Fancy - ${urlToPageName(props.pageContext.pageUrl)}`}
				htmlAttributes={{ lang: "en" }}
			>
				<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon-agentic-ai-2025.png" />
				<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32-agentic-ai-2025.png" />
				<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16-agentic-ai-2025.png" />
				<link rel="manifest" href="/site-agentic-ai-2025.webmanifest" />
				<link rel="mask-icon" href="/safari-pinned-tab-agentic-ai-2025.svg" color="#5bbad5" />
				<link rel="shortcut icon" href="/favicon.ico" />
				<meta name="msapplication-TileColor" content="#2d89ef" />
				<meta name="msapplication-config" content="/browserconfig-agentic-ai-2025.xml" />
				<meta name="theme-color" content="#ffffff" />
			</Helmet>
			<div className={`${scss.root} fancy-root`}>
				<a className={scss.skipToMain} href="#main-content">
					Skip to content
				</a>
				<StyleguideSideMenu pageUrl={props.pageContext.pageUrl} />
				<main id="main-content" ref={mainRef} className={scss.main} tabIndex={0}>
					{props.children}
				</main>
				{props.pageContext.notepad && <DocsNotepad src={props.pageContext.notepad} />}
			</div>
		</styleguideCtx.Provider>
	);
}

if (typeof document !== "undefined") {
	document.body.classList.add("fancy-root");
}

//Analytics Script
const SZ_ID = "SI_ANALYTICS";
if (typeof document !== "undefined" && !document.getElementById(SZ_ID)) {
	const sz = document.createElement("script");
	sz.type = "text/javascript";
	sz.async = true;
	sz.src = "//siteimproveanalytics.com/js/siteanalyze_6276094.js";
	sz.id = SZ_ID;
	document.body.appendChild(sz);
}

//Adding error listener
addErrorListener("docs-layout", (msg) => console.error(msg));
